import { useState } from "react";
import { ImageUpload, MeasurementUnit, BodyMeasurementResult } from "../types/unified-services";

const MIN_HEIGHT_CM = 51;
const MAX_HEIGHT_CM = 230;
const CM_TO_INCH = 0.393701;
const INCH_TO_CM = 2.54;

const createInitialImageUpload = (): ImageUpload => ({
  file: null,
  base64: null,
  preview: null,
  isValid: false,
  error: undefined,
  blurredBase64: null,
});

export const useBodyMeasurementState = () => {
  const [frontImage, setFrontImage] = useState<ImageUpload>(createInitialImageUpload);
  const [sideImage, setSideImage] = useState<ImageUpload>(createInitialImageUpload);

  const [height, setHeight] = useState<number | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<MeasurementUnit>("cm");
  const [displayValue, setDisplayValue] = useState<string | null>(null);

  const [measurementData, setMeasurementData] = useState<BodyMeasurementResult | null>(null);

  const [availableCharts, setAvailableCharts] = useState<any | null>(null);
  const [sizeGuideResult, setSizeGuideResult] = useState<any | null>(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [loadingSizeGuide, setLoadingSizeGuide] = useState(false);
  const [isClosestMatch, setIsClosestMatch] = useState<string | null>(null);
  const [cameraOptedOut, setCameraOptedOut] = useState(false);

  const getValidationProps = () => {
    if (selectedUnit === "inch") {
      return {
        min: Math.round(MIN_HEIGHT_CM * CM_TO_INCH),
        max: Math.round(MAX_HEIGHT_CM * CM_TO_INCH),
        step: 0.1,
      };
    }
    return {
      min: MIN_HEIGHT_CM,
      max: MAX_HEIGHT_CM,
      step: 1,
    };
  };

  const convertValue = (value: number): string => {
    if (selectedUnit === "inch") {
      return (value * CM_TO_INCH).toFixed(2);
    }
    return value.toString();
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);

    if (inputValue === "") {
      return;
    }

    const numericValue = Number(inputValue);
    const cmValue = selectedUnit === "inch" ? Math.round(numericValue * INCH_TO_CM) : numericValue;
    setHeight(cmValue);
  };

  const handleUnitChange = (unit: MeasurementUnit) => {
    if (displayValue === "" || displayValue === null) {
      setSelectedUnit(unit);
      return;
    }
    const currentValue = Number(displayValue);
    const newValue = unit === "inch" ? (currentValue * CM_TO_INCH).toFixed(2) : Math.round(currentValue * INCH_TO_CM).toString();

    setSelectedUnit(unit);
    setDisplayValue(newValue);
  };

  const resetBodyMeasurement = () => {
    setFrontImage(createInitialImageUpload());
    setSideImage(createInitialImageUpload());
    setHeight(null);
    setSelectedUnit("cm");
    setDisplayValue(null);
    setMeasurementData(null);
    setAvailableCharts(null);
    setSizeGuideResult(null);
    setLoadingCharts(false);
    setLoadingSizeGuide(false);
    setIsClosestMatch(null);
    setCameraOptedOut(false);
  };

  const getImagesForService = () => {
    const extractBase64 = (base64String: string | null): string | null => {
      if (!base64String) return null;

      if (base64String.startsWith("data:")) {
        const commaIndex = base64String.indexOf(",");
        return commaIndex !== -1 ? base64String.substring(commaIndex + 1) : base64String;
      }

      return base64String;
    };

    const frontImageData = frontImage.blurredBase64 || frontImage.base64;
    const sideImageData = sideImage.blurredBase64 || sideImage.base64;

    return {
      frontImage: extractBase64(frontImageData),
      sideImage: extractBase64(sideImageData),
    };
  };

  const getOriginalImages = () => {
    return {
      frontImage: frontImage.base64,
      sideImage: sideImage.base64,
    };
  };

  const hasBlurredImages = () => {
    return !!(frontImage.blurredBase64 && sideImage.blurredBase64);
  };

  const clearBlurredImages = () => {
    setFrontImage({
      ...frontImage,
      blurredBase64: null,
    });
    setSideImage({
      ...sideImage,
      blurredBase64: null,
    });
  };

  return {
    frontImage,
    sideImage,
    height,
    selectedUnit,
    displayValue,
    measurementData,
    availableCharts,
    sizeGuideResult,
    loadingCharts,
    loadingSizeGuide,
    isClosestMatch,
    cameraOptedOut,

    setFrontImage,
    setSideImage,
    setHeight,
    setSelectedUnit,
    setDisplayValue,
    setMeasurementData,
    setAvailableCharts,
    setSizeGuideResult,
    setLoadingCharts,
    setLoadingSizeGuide,
    setIsClosestMatch,
    setCameraOptedOut,
    resetBodyMeasurement,

    getValidationProps,
    convertValue,
    handleHeightChange,
    handleUnitChange,
    getImagesForService,
    getOriginalImages,
    hasBlurredImages,
    clearBlurredImages,
  };
};
